/**
 * Log-safe reductions shared by the API and the browser (spec `log-safety`, openspec change
 * `client-telemetry` D3): no Node or DOM API beyond `URL`, so the SPA reduces exactly as the API
 * does. `packages/core/src/safe-error.ts` describes errors with {@link safeMessage};
 * `apps/api/src/csp.ts` and the client events reduce paths with {@link reducePath}.
 */

/** Longest reduced path. */
const MAX_PATH = 200;
/** Path segments kept as they are: lowercase route words and static file names. */
const ROUTE_WORD = /^[a-z][a-z-]{0,31}$/;
const STATIC_FILE = /^[\w.-]{1,96}\.(?:js|mjs|css|html|map|json|woff2?|svg|png|ico|webmanifest)$/;

/**
 * A path with every segment that could be an identifier replaced by `:id` (UUIDs, interaction
 * UIDs, tokens): only route words and static file names are kept. Capped.
 */
export function reducePath(pathname: string): string {
  return pathname
    .split("/")
    .map((s) => (s === "" || ROUTE_WORD.test(s) || STATIC_FILE.test(s) ? s : ":id"))
    .join("/")
    .slice(0, MAX_PATH);
}

/** An error's `name` (AWS `ProvisionedThroughputExceededException`: 38). */
export const ERROR_NAME = /^[A-Za-z][A-Za-z0-9_-]{0,47}$/;
/**
 * Shaped like a credential rather than a name, whatever an allowlist says: a known secret prefix
 * (GitHub `ghp_`…, AWS `AKIA`/`ASIA`, GitLab `glpat-`, Slack `xox?-`) or a run of 16+ letters and
 * digits with letters and at least two digits (a known code or name splits its words with `_` or
 * `-`, or carries one digit at most: AWS `EC2ThrottledException`). Heuristic: an opaque run with
 * fewer than two digits still passes, and so does a digits-only run after a letter-led prefix
 * (`ERR-1234567890123456`; residual-risks 44).
 */
export const OPAQUE =
  /^(?:gh[pousr]_|github_pat_|AKIA|ASIA|glpat-|xox[abposr]-)|(?=(?:[A-Za-z]*\d){2})(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]{16}/;

/** An identifier-shaped, non-credential error name, else `Error`. */
export function safeErrorName(name: unknown): string {
  return typeof name === "string" && ERROR_NAME.test(name) && !OPAQUE.test(name) ? name : "Error";
}

/** Longest message kept (then `…`). */
export const MAX_SAFE_MESSAGE = 200;
/** The part of a message's first line read at all: anything past the cap never reaches a line. */
const MAX_SCANNED = 2 * MAX_SAFE_MESSAGE;
/**
 * A quoted segment, escape-aware (`"a \"b\" c"` is one segment), and unclosed ones to the end
 * of the line (`invalid value "…`, or a quote cut open by {@link MAX_SCANNED}). An apostrophe
 * after a letter is a contraction (`can't connect`), not a quote.
 */
const QUOTED =
  /"(?:[^"\\]|\\[\s\S]?)*(?:"|$)|(?<![A-Za-z])'(?:[^'\\]|\\[\s\S]?)*(?:'|$)|`(?:[^`\\]|\\[\s\S]?)*(?:`|$)|“[^”]*(?:”|$)|«[^»]*(?:»|$)/g;
const SCHEME_CHAR = /[A-Za-z0-9+.-]/;
/**
 * Two or three dot-separated runs of at least 20 token characters: a JWT or a dotted API key,
 * also inside a token (`token=eyJ…`, `(eyJ…)`, `Bearer:eyJ…`). A JWT is also caught by its
 * header alone (`eyJ`, base64url of `{"`, then 8 or more token characters: `{"alg":"none"}` is
 * 19) when no letter or digit precedes it (`id-eyJ…`, `jwt_eyJ…` included), with every segment
 * after it (a JWE has five) and an empty signature. A version (`v24.1.0`) or a file name has
 * short segments and is kept.
 */
const DOTTED_TOKEN =
  /(?<![A-Za-z0-9])eyJ[A-Za-z0-9_-]{8,}(?:\.[A-Za-z0-9_-]*)+|[A-Za-z0-9_-]{20,}(?:\.[A-Za-z0-9_-]{20,}){1,2}/g;
/** Five digits or more in a row: an order, phone or account number, never a status or a count. */
const DIGIT_RUN = /\d{5,}/g;

export interface SafeMessageOptions {
  /**
   * Also redacts runs of five or more digits (client error messages, openspec change
   * `client-telemetry` D3). Off for the API's own error lines, which keep their exact shape.
   */
  digitRuns?: boolean;
}

/**
 * The first line of a message, quoted segments, dotted token runs, e-mail addresses and URLs'
 * paths, queries and credentials redacted, capped. A heuristic for errors of ESI's own code and
 * libraries; database errors never get here. Linear in the message: it may be megabytes long and
 * is described on the event loop.
 */
export function safeMessage(
  message: unknown,
  options: SafeMessageOptions = {},
): string | undefined {
  if (typeof message !== "string") {
    return undefined;
  }
  const end = message.indexOf("\n");
  const first = end < 0 ? message : message.slice(0, end);
  const tokens = first.slice(0, MAX_SCANNED).replace(QUOTED, "…").split(/(\s+)/);
  if (first.length > MAX_SCANNED) {
    // The token cut by the bound may be part of an e-mail address missing what marks it.
    tokens[tokens.length - 1] = "…";
  }
  let line = tokens.map(safeToken).join("").trim();
  if (options.digitRuns) {
    line = line.replace(DIGIT_RUN, "…");
  }
  if (!line) {
    return undefined;
  }
  return line.length > MAX_SAFE_MESSAGE ? `${line.slice(0, MAX_SAFE_MESSAGE)}…` : line;
}

/**
 * A whitespace-free token: dotted token runs go first, then a URL keeps what precedes it and its
 * origin (`(https://host/…`, `url=postgres://…`), an e-mail-shaped one (`@`, then a dot) goes.
 */
function safeToken(raw: string): string {
  const token = raw.replace(DOTTED_TOKEN, "…");
  const separator = token.indexOf("://");
  if (separator >= 0) {
    // The scheme: the run of scheme characters before `://`.
    let start = separator;
    while (start > 0 && SCHEME_CHAR.test(token.charAt(start - 1))) {
      start -= 1;
    }
    // What precedes it goes through the same rule (`alice@corp.example,https://…`).
    const prefix = token.slice(0, start).includes("@") ? "…" : token.slice(0, start);
    try {
      const origin = new URL(token.slice(start)).origin;
      return origin === "null" ? `${prefix}…` : `${prefix}${origin}/…`;
    } catch {
      return `${prefix}…`;
    }
  }
  const at = token.indexOf("@");
  return at >= 0 && token.indexOf(".", at) > at ? "…" : token;
}
